import React, { useCallback, useEffect, useRef, useState, memo } from 'react';

import { Link } from 'react-router';
import { WebMercatorViewport } from '@math.gl/web-mercator';

import ReactMapGL, { Marker, NavigationControl, Popup } from 'react-map-gl';

import 'mapbox-gl/dist/mapbox-gl.css';
import { NO_SITES_MESSAGE } from '../../constants';
import { useClientConfig } from '../App/App';
import SiteThumbnail from '../SiteThumbnail';

const SELECTED_ZOOM = 16.7;
const MAP_CONTROLS = ['dragPan', 'dragRotate', 'scrollZoom', 'touchZoom', 'touchRotate'];

const getMarkerColorClass = (properties, { markers = [] }) => {
  const { colors: markerColors = [] } = markers;

  let theColor = 'red';
  for (const markerColor of markerColors) {
    const { property: propertyAttribute, value, color } = markerColor;
    const property = properties.find(({ attribute }) => attribute === propertyAttribute);

    if (property !== undefined && property.value === value) {
      theColor = color;
    }
  }
  return theColor;
};

const transition = (transitionDuration = 1000) => ({
  transitionDuration,
});

const zoomViewport = (viewport, sites, mapWidth, mapHeight) => {
  if (sites.length === 0) {
    return viewport;
  }

  if (sites.length === 1) {
    const {
      location: { lng, lat },
    } = sites[0];

    return {
      ...viewport,
      ...transition(),
      latitude: lat,
      longitude: lng,
      pitch: 0,
      bearing: 0,
    };
  }

  if (sites.length > 1) {
    const bbox = sites.reduce(
      ([[minLng, minLat], [maxLng, maxLat]], { location: { lat, lng } }) => [
        [Math.min(minLng, lng), Math.min(minLat, lat)],
        [Math.max(maxLng, lng), Math.max(maxLat, lat)],
      ],
      [
        [sites[0].location.lng, sites[0].location.lat],
        [sites[0].location.lng, sites[0].location.lat],
      ]
    );

    const completeViewport = {
      width: mapWidth,
      height: mapHeight,
      latitude: sites[0].location.lat,
      longitude: sites[0].location.lng,
      zoom: 1,
      ...viewport,
    };

    const wmviewport = new WebMercatorViewport(completeViewport);
    const { longitude, latitude, zoom } = wmviewport.fitBounds(bbox, {
      padding: 100,
    });
    return {
      ...viewport,
      ...transition(),
      height: mapHeight,
      width: mapWidth,
      longitude,
      latitude,
      zoom,
      pitch: 0,
      bearing: 0,
    };
  }

  return {
    ...viewport,
  };
};

const SitePopup = ({
  site: {
    name,
    properties,
    location: { lat, lng },
    thumbnail,
  },
}) => (
  <Popup
    longitude={lng}
    latitude={lat}
    closeButton={false}
  >
    <div className="map-popup">
      <div className="thumbnail">
        <SiteThumbnail
          src={thumbnail}
          name={name}
        />
      </div>
      <div>
        <div className="site-name">{name}</div>
        {properties
          .filter(({ category }) => category === 'general')
          .map(({ attribute, name: propertyName, value }) => (
            <div
              key={attribute}
              className="property"
            >
              <div className="name">{propertyName}</div>
              <div className="value">{value}</div>
            </div>
          ))}
      </div>
    </div>
  </Popup>
);

const Map = memo(({ sites, isReady, nightmode }) => {
  const [popupSite, setPopupSite] = useState(null);
  const [viewport, setViewport] = useState({});
  const [controls, setControls] = useState([]);
  const { mapboxAccessToken } = useClientConfig();

  const mapContainerRef = useRef({});
  const { clientHeight: mapHeight, clientWidth: mapWidth } = mapContainerRef.current;
  const mapReference = useRef();

  const resize = useCallback(() => {
    if (mapContainerRef.current) {
      const { width, height } = mapContainerRef.current.getBoundingClientRect();
      setViewport((vp) => ({ ...vp, width, height }));
    }
  }, [mapContainerRef]);

  let resizeTimer;

  const handleResize = useCallback(() => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => resize(), 200);
  }, [resize]);

  useEffect(() => {
    resize();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (sites && mapWidth && mapHeight) {
      setViewport((vp) => zoomViewport(vp, sites, mapWidth, mapHeight));
      setControls(MAP_CONTROLS);
    }
  }, [sites, mapWidth, mapHeight]);

  const onLoad = useCallback(() => {
    const map = mapReference.current.getMap();

    map.addLayer(
      {
        id: 'extruded-buildings',
        source: 'composite',
        'source-layer': 'building',
        filter: ['==', 'extrude', 'true'],
        type: 'fill-extrusion',
        minZoom: SELECTED_ZOOM,
        paint: {
          'fill-extrusion-color': '#ccc',
          'fill-extrusion-height': ['get', 'height'],
        },
      },
      'waterway-label'
    );

    if (sites && mapWidth && mapHeight) {
      setViewport((vp) => zoomViewport(vp, sites, mapWidth, mapHeight));
    }
  }, [sites, mapWidth, mapHeight]);

  const handleMouseMove = useCallback(
    (event) => {
      const domEvent = event && event.originalEvent ? event.originalEvent : event;
      const target = domEvent && domEvent.target ? domEvent.target : null;

      if (!target) return;

      const markerElement = target.closest('.site-marker');

      if (markerElement && markerElement.id) {
        const markerId = markerElement.id;
        if (popupSite === null || popupSite.id !== markerId) {
          const found = sites.find((s) => s.id === markerId);
          if (found) setPopupSite(found);
        }
      } else if (target.classList && target.classList.contains('overlays')) {
        setPopupSite(null);
      }
    },
    [popupSite, sites]
  );

  const markers = sites.map((item) => {
    const {
      location: { lat, lng },
      id,
      properties,
      layout,
    } = item;
    const size = 24;
    return (
      <Marker
        key={id}
        longitude={lng}
        latitude={lat}
        offsetLeft={-(size / 2)}
        offsetTop={-(size / 2)}
        captureClick
      >
        <Link to={`/sites/${id}`}>
          <div
            id={id}
            className={`site-marker ${getMarkerColorClass(properties, layout)}`}
            style={{
              width: size,
              height: size,
            }}
          >
            <svg
              viewBox="0 0 100 100"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                fill="currentColor"
                fillOpacity="0.2"
                cx="50"
                cy="50"
                r="50"
              />
              <circle
                fill="currentColor"
                cx="50"
                cy="50"
                r="30"
              />
            </svg>
          </div>
        </Link>
      </Marker>
    );
  });

  return (
    <div
      className="map-component"
      ref={mapContainerRef}
    >
      {isReady && sites.length === 0 && <div className="no-sites-overlay">{NO_SITES_MESSAGE}</div>}
      <ReactMapGL
        {...controls}
        {...viewport}
        ref={mapReference}
        mapStyle={`mapbox://styles/mapbox/${nightmode ? 'dark-v10' : 'light-v8'}`}
        mapboxAccessToken={mapboxAccessToken}
        attributionControl={false}
        onLoad={onLoad}
        onMove={(event) => setViewport(event.viewState)}
        onMouseMove={handleMouseMove}
      >
        {markers}
        {popupSite === null ? null : <SitePopup site={popupSite} />}
        <div
          data-testid="map-controls"
          className="map-controls-wrapper"
        >
          <NavigationControl />
        </div>
      </ReactMapGL>
    </div>
  );
});

export default Map;
